import { useMemo } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';
import type { LeaderboardEntry } from '@kangaroo/shared';
import { UserRole } from '@kangaroo/shared';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { PageHeader, SectionCard } from '@kangaroo/ui';

import { CircuitDetailSkeleton } from '../../components/loading/PageSkeletons';
import {
  DETAILED_CIRCUIT_DEFAULT_LAYERS,
  DetailedCircuitMap,
} from '../../components/dev/TrackCatalogMap';
import { GpsSpeedTrackMap } from '../../components/telemetry/GpsSpeedTrackMap';
import { fetchLeaderboard } from '../../features/leaderboard';
import { fetchTelemetryTracks } from '../../features/telemetryContext';
import { useMeasurementPreferences } from '../../hooks/useMeasurementPreferences';
import {
  buildContributionCircuitItem,
  formatCircuitDirection,
  leaderboardForCircuit,
} from '../../lib/circuits';
import { circuitDetailRequestKeys } from '../../lib/circuitDetailRequests';
import { buildCircuitMapOverlay } from '../../lib/circuitMapOverlay';
import { formatLapTime } from '../../lib/lapFormat';
import { buildTrackCatalogEditUrl } from '../../lib/trackCatalogEditorLoad';
import { PAGE_ICONS } from '../../lib/pageIcons';
import { formatDistanceFromKm } from '../../lib/telemetryMeasurements';
import {
  fetchTrackContributionById,
  fetchGlobalTrackContributions,
} from '../../lib/trackContributions';
import { useAuthStore } from '../../store/authStore';

function formatDate(recordedAtMs: number | null) {
  if (recordedAtMs === null) return 'Date unknown';
  return new Date(recordedAtMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function LeaderboardRow({ entry, rank, bestLapMs }: {
  entry: LeaderboardEntry;
  rank: number;
  bestLapMs: number;
}) {
  const gapMs = entry.lapTimeMs - bestLapMs;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px minmax(82px, 0.65fr) minmax(0, 1.35fr)',
        gap: 1,
        alignItems: 'center',
        px: { xs: 1, sm: 1.5 },
        py: 1.1,
        borderBottom: '1px solid rgba(148,163,184,0.12)',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Typography color={rank <= 3 ? 'warning.main' : 'text.secondary'} fontWeight={900}>
        {rank}
      </Typography>
      <Box>
        <Typography fontWeight={900} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatLapTime(entry.lapTimeMs)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {rank === 1 ? 'Best lap' : `+${(gapMs / 1000).toFixed(3)}`}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={750} noWrap>
          {[entry.driverName, entry.kartLabel].filter(Boolean).join(' · ') || 'Team session'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {formatDate(entry.recordedAtMs)}{entry.lapNumber !== null ? ` · Lap ${entry.lapNumber}` : ''}
        </Typography>
      </Box>
    </Box>
  );
}

export function CircuitDetailPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const measurementPreferences = useMeasurementPreferences();
  const { circuitId = '' } = useParams<{ circuitId: string }>();
  const requests = circuitDetailRequestKeys(circuitId);
  const decodedCircuitId = requests.circuitId;

  const { data: contribution, isLoading: contributionLoading } = useSWR(
    requests.contribution,
    decodedCircuitId ? () => fetchTrackContributionById(decodedCircuitId) : null,
    { revalidateOnFocus: false },
  );
  const { data: leaderboard = [] } = useSWR(
    requests.leaderboard,
    fetchLeaderboard,
    { revalidateOnFocus: false },
  );
  const { data: tracks } = useSWR(
    requests.tracks,
    fetchTelemetryTracks,
  );
  const { data: contributionSummaries } = useSWR(
    requests.summaries,
    () => fetchGlobalTrackContributions('summary'),
    { revalidateOnFocus: false },
  );

  const track = tracks?.find((candidate) => candidate.id === contribution?.trackId) ?? null;
  const circuit = useMemo(
    () => contribution ? buildContributionCircuitItem(contribution, track) : null,
    [contribution, track],
  );
  const overlay = useMemo(
    () => contribution ? buildCircuitMapOverlay(contribution) : null,
    [contribution],
  );
  const circuitLeaderboard = circuit ? leaderboardForCircuit(circuit, leaderboard) : null;
  const configurations = useMemo(
    () => (contributionSummaries ?? [])
      .filter((candidate) => candidate.trackId === contribution?.trackId)
      .sort((left, right) => (left.layoutName ?? '').localeCompare(right.layoutName ?? '')),
    [contribution?.trackId, contributionSummaries],
  );
  const loading = contributionLoading;
  const canEditCircuit = user?.role === UserRole.TEAM_OWNER || user?.role === UserRole.COACH;

  const mapViewport = useMemo(() => {
    const points = contribution?.payload.gpsPolyline ?? [];
    if (points.length === 0) return undefined;
    const center = points.reduce(
      (sum, point) => ({ lat: sum.lat + point.lat, lon: sum.lon + point.lon }),
      { lat: 0, lon: 0 },
    );
    return { lat: center.lat / points.length, lon: center.lon / points.length, zoom: 17 };
  }, [contribution]);

  if (!decodedCircuitId) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: 1480, mx: 'auto' }}>
        <SectionCard>
          <Typography fontWeight={800} sx={{ mb: 0.75 }}>
            Invalid circuit link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This circuit link is incomplete or malformed.
          </Typography>
          <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate('/circuits')}>
            Back to circuits
          </Button>
        </SectionCard>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: 1480, mx: 'auto' }}>
        <CircuitDetailSkeleton />
      </Box>
    );
  }

  if (!circuit || !contribution || !overlay) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: 1480, mx: 'auto' }}>
        <SectionCard>
          <Typography fontWeight={800} sx={{ mb: 0.75 }}>Circuit not found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This published circuit may have been removed or the link is outdated.
          </Typography>
          <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate('/circuits')}>
            Back to circuits
          </Button>
        </SectionCard>
      </Box>
    );
  }

  const address = contribution.location?.address?.trim() || track?.address?.trim();
  const subtitle = [circuit.layoutName, address].filter(Boolean).join(' · ') || 'Published circuit layout';

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: 1480, mx: 'auto' }}>
      <Stack spacing={2}>
        <PageHeader
          title={circuit.name}
          subtitle={subtitle}
          icon={PAGE_ICONS.circuits}
          mb={0}
          breadcrumbs={[
            { label: 'Circuits', onClick: () => navigate('/circuits') },
            { label: circuit.name },
          ]}
          actions={(
            <Stack direction="row" spacing={1}>
              {canEditCircuit ? (
                <Button
                  component={RouterLink}
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  to={buildTrackCatalogEditUrl(contribution.id)}
                >
                  Edit layout
                </Button>
              ) : null}
              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={() => navigate('/circuits')}
              >
                All circuits
              </Button>
            </Stack>
          )}
        />

        {configurations.length > 1 ? (
          <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
            <InputLabel>Configuration</InputLabel>
            <Select
              label="Configuration"
              value={contribution.id}
              onChange={(event) => navigate(`/circuits/${encodeURIComponent(event.target.value)}`)}
            >
              {configurations.map((configuration) => (
                <MenuItem key={configuration.id} value={configuration.id}>
                  {configuration.layoutName || 'Default configuration'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                height: { xs: 380, sm: 520, lg: 680 },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
                border: '1px solid rgba(148,163,184,0.18)',
              }}
            >
              {contribution.payload.detailedGeometry ? (
                <DetailedCircuitMap
                  geometry={contribution.payload.detailedGeometry}
                  geometryKey={contribution.id}
                  layers={DETAILED_CIRCUIT_DEFAULT_LAYERS}
                  minHeight={0}
                />
              ) : (
                <GpsSpeedTrackMap
                  fill
                  points={overlay.referenceTrackGroups[0] ?? []}
                  showLapLines={false}
                  showLegend={false}
                  mapViewport={mapViewport}
                  mapMarkers={overlay.mapMarkers}
                  mapMarkerScale={1.35}
                  startFinishLine={overlay.startFinishLine}
                  fitBoundsPadding={52}
                  maxFitZoom={19}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <SectionCard title="Circuit details">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">Direction</Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {formatCircuitDirection(circuit.direction)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Length</Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {circuit.lapLengthM
                        ? formatDistanceFromKm(circuit.lapLengthM / 1000, measurementPreferences, 2)
                        : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Turns</Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {circuit.cornerCount ?? '—'}
                    </Typography>
                  </Box>
                </Box>
                {address ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.15 }} />
                    <Typography variant="body2" color="text.secondary">{address}</Typography>
                  </Stack>
                ) : null}
                {contribution.notes?.trim() ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, mt: 1.5 }}>
                    {contribution.notes.trim()}
                  </Typography>
                ) : null}
              </SectionCard>

            <SectionCard
              title="Team leaderboard"
              subtitle={circuitLeaderboard
                ? `${circuitLeaderboard.sessionCount} recorded session${circuitLeaderboard.sessionCount === 1 ? '' : 's'}`
                : 'No recorded laps at this circuit yet'}
              action={<EmojiEventsOutlinedIcon sx={{ color: circuitLeaderboard ? 'warning.main' : 'text.secondary' }} />}
              sx={{ flex: 1 }}
              contentSx={{ p: circuitLeaderboard ? 0 : 2 }}
            >
              {circuitLeaderboard ? (
                <Stack spacing={0}>
                  {circuitLeaderboard.entries.map((entry, index) => (
                    <LeaderboardRow
                      key={`${entry.sessionKey}-${entry.lapNumber ?? 'best'}`}
                      entry={entry}
                      rank={index + 1}
                      bestLapMs={circuitLeaderboard.bestLapMs}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack alignItems="center" spacing={0.75} sx={{ py: 3, textAlign: 'center' }}>
                  <EmojiEventsOutlinedIcon sx={{ fontSize: 30, color: 'text.secondary' }} />
                  <Typography fontWeight={800}>No leaderboard times yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Times will appear after your team records a session at this circuit.
                  </Typography>
                </Stack>
              )}
            </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
